import React from "react";
import { useState } from "react";
import { useEffect } from "react";

import axios from "axios";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import LoadingButton from "@mui/lab/LoadingButton";

import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";
import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import ConfiguredForm from "../UtilityComponents/FormUtilityComponents/ConfiguredForm";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import FormIsValid from "../UtilityComponents/FormUtilityComponents/FormIsValid";

import ConfirmDialogProps from "../../NavigationComponents/ConfirmDialogProps";

import { useSearchParams } from "react-router-dom";

import GetCSRFToken from "../../AuthComponents/GetCSRFToken";

type ProfileRequiredPaperProps = {
  setConfirmOnNavigateAway: (confirmOnNavigateAway: Partial<ConfirmDialogProps> | false) => void;
  backendInputRowStateList?: FormRowState[];
  setBackendInputRowStateList: (backendInputRowStateList: FormRowState[] | undefined) => void;
  profileConfig: FormConfig | undefined;
  inputRowStateList: FormRowState[];
  setInputRowStateList: (newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) => void;
  handleUpdateNotification: () => void;
};

function ProfileRequiredPaper(props: ProfileRequiredPaperProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamEdit = searchParams.get("edit")?.toLowerCase();

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editMode, setEditMode] = useState(searchParamEdit && searchParamEdit === "true" ? true : false);

  useEffect(() => {
    setSearchParams((oldParams) => {
      oldParams.delete("edit");
      return oldParams;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (props.backendInputRowStateList === undefined) {
      props.setConfirmOnNavigateAway({
        confirmationTitle: "Profile Incomplete :(",
        confirmationMessage: "Please complete your profile before exploring the rest of the site!",
        cancelButtonMessage: "Finish Profile",
        cancelButtonIcon: <AccountCircleIcon />,
        cancelButtonColor: "primary",
        confirmButtonMessage: "Explore Anyway",
        confirmButtonIcon: null,
        confirmButtonColor: "error",
        confirmOnLeft: true,
      });
    } else if (editMode) {
      props.setConfirmOnNavigateAway({
        confirmationTitle: "Discard Changes?",
        confirmationMessage: "Are you sure you want to navigate away from this page? Any edits will be lost.",
        cancelButtonMessage: "Resume Editing",
        cancelButtonIcon: null,
        cancelButtonColor: "primary",
        confirmButtonMessage: "Discard Changes",
        confirmButtonIcon: null,
        confirmButtonColor: "error",
        confirmOnLeft: true,
      });
    } else {
      props.setConfirmOnNavigateAway(false);
    }
  }, [editMode, props.backendInputRowStateList]);

  //   const [beenEdited, setBeenEdited] = useState(false);

  //   useEffect(() => setBeenEdited(editMode), [backendInputRowStateList]); //

  const disableSubmit =
    !FormIsValid(props.inputRowStateList, props.profileConfig) ||
    (!editMode && props.backendInputRowStateList !== undefined);

  function handleSubmit() {
    setLoading(true);

    axios
      .post(
        "/api/userprofile/profile",
        { params: props.inputRowStateList },
        {
          headers: { "X-CSRFToken": GetCSRFToken() },
        }
      )
      .then((res) => {
        props.handleUpdateNotification();
      })
      .catch((error) => {
        props.setInputRowStateList(props.backendInputRowStateList || []);
        if (process.env.NODE_ENV === "development") {
          console.log(error.response);
        } // TODO: communicate about the error, this just rolls it back for now
      })
      .finally(() => {
        setEditMode(false);
        setLoading(false);
      });
  }

  return (
    <Stack
      direction="column"
      width="100%"
      height="100%"
      overflow="auto"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 1,
      }}
    >
      <Typography variant="h5" noWrap sx={{ color: "info.main", alignSelf: "flex-start" }}>
        Core Profile Information
      </Typography>
      <Box sx={{ flexGrow: 0, width: "100%", maxWidth: "500px" }}>
        <ConfiguredForm
          config={props.profileConfig}
          refreshing={refreshing}
          setRefreshing={setRefreshing}
          disabled={!editMode && props.backendInputRowStateList !== undefined} // TODO: make this graphics a bit nicer when disabled
          state={props.inputRowStateList}
          setState={props.setInputRowStateList}
          canSubmit={!disableSubmit && !loading}
          handleSubmit={handleSubmit}
        />
      </Box>
      <Box
        sx={{ flexGrow: 1, width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}
      >
        {props.backendInputRowStateList && (
          <Button
            disabled={loading}
            startIcon={editMode ? <CloseIcon /> : <EditIcon />}
            onClick={() => {
              if (editMode) {
                props.setInputRowStateList(props.backendInputRowStateList || []);
                setEditMode(false);
              } else {
                setEditMode(true);
              }
            }}
          >
            {editMode ? "Cancel Edit" : "Edit Profile"}
          </Button>
        )}
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={disableSubmit}
          startIcon={props.backendInputRowStateList ? <AccountCircleIcon /> : <SaveIcon />}
          onClick={handleSubmit}
        >
          {props.backendInputRowStateList ? "Save Updated Profile" : "Create Profile"}
        </LoadingButton>
      </Box>
    </Stack>
  );
}

export default ProfileRequiredPaper;
